import React from 'react';
import ContactForm from '../components/ContactForm';
import StepCard from '../components/StepCard';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useSEO } from '../hooks/useSEO';
import Silk from '../components/Silk';
// Import delle icone SVG con colore primario
const HandCoinsIcon = () => (
  <img src="/assets/icons/hand-coins-bold.svg" alt="Hand Coins" className="w-6 h-6" style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1352%) hue-rotate(213deg) brightness(89%) contrast(101%)' }} />
);
const CertificateIcon = () => (
  <img src="/assets/icons/certificate-bold.svg" alt="Certificate" className="w-6 h-6" style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1352%) hue-rotate(213deg) brightness(89%) contrast(101%)' }} />
);
const SparkleIcon = () => (
  <img src="/assets/icons/sparkle-bold.svg" alt="Sparkle" className="w-6 h-6" style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1352%) hue-rotate(213deg) brightness(89%) contrast(101%)' }} />
);
const TagIcon = () => (
  <img src="/assets/icons/tag-bold.svg" alt="Tag" className="w-6 h-6" style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1352%) hue-rotate(213deg) brightness(89%) contrast(101%)' }} />
);

// Componente per le card dei benefici con animazione
const BenefitCard: React.FC<{ 
  benefit: { text: string; description: string; icon: React.ReactNode }; 
  index: number; 
}> = ({ benefit, index }) => {
  const { elementRef, isVisible } = useScrollAnimation({
    threshold: 0.1,
    triggerOnce: true,
    delay: 0
  });

  return (
    <div 
      ref={elementRef as React.RefObject<HTMLDivElement>}
      className="bg-white rounded-2xl px-8 py-4 md:p-8 text-center transition-all duration-300"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
      }}
    >
      <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-6 mx-auto">
        {benefit.icon}
      </div>
      <h3 className="text-xl md:text-2xl text-balance font-bold text-gray-900 mb-4">
        {benefit.text}
      </h3>
      <p className="text-md md:text-lg text-balance text-gray-600 leading-relaxed">
        {benefit.description}
      </p>
    </div>
  );
};

// Componente per le feature del portale con animazione
const PortalFeatureCard: React.FC<{ 
  feature: string; 
  index: number; 
}> = ({ feature, index }) => {
  const { elementRef, isVisible } = useScrollAnimation({
    threshold: 0.1,
    triggerOnce: true,
    delay: index * 100
  });

  return (
    <div 
      ref={elementRef as React.RefObject<HTMLDivElement>}
      className="flex flex-col items-center text-center px-6 py-4 md:px-0 md:pt-10 md:pb-6"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
      }}
    >
      <div className="mb-4">
        <svg 
          className="w-14 h-14 text-tertiary" 
          viewBox="0 0 256 256" 
          fill="currentColor"
        >
          <path d="M228.75,100.05c-3.52-3.67-7.15-7.46-8.34-10.33-1.06-2.56-1.14-7.83-1.21-12.47-.15-10-.34-22.44-9.18-31.27s-21.27-9-31.27-9.18c-4.64-.07-9.91-.15-12.47-1.21-2.87-1.19-6.66-4.82-10.33-8.34C148.87,20.46,140.05,12,128,12s-20.87,8.46-27.95,15.25c-3.67,3.52-7.46,7.15-10.33,8.34-2.56,1.06-7.83,1.14-12.47,1.21C67.25,37,54.81,37.14,46,46S37,67.25,36.8,77.25c-.07,4.64-.15,9.91-1.21,12.47-1.19,2.87-4.82,6.66-8.34,10.33C20.46,107.13,12,116,12,128S20.46,148.87,27.25,156c3.52,3.67,7.15,7.46,8.34,10.33,1.06,2.56,1.14,7.83,1.21,12.47.15,10,.34,22.44,9.18,31.27s21.27,9,31.27,9.18c4.64.07,9.91.15,12.47,1.21,2.87,1.19,6.66,4.82,10.33,8.34C107.13,235.54,116,244,128,244s20.87-8.46,27.95-15.25c3.67-3.52,7.46-7.15,10.33-8.34,2.56-1.06,7.83-1.14,12.47-1.21,10-.15,22.44-.34,31.27-9.18s9-21.27,9.18-31.27c.07-4.64.15-9.91,1.21-12.47,1.19-2.87,4.82-6.66,8.34-10.33C235.54,148.87,244,140.05,244,128S235.54,107.13,228.75,100.05Zm-17.32,39.29c-4.82,5-10.28,10.72-13.19,17.76-2.82,6.8-2.93,14.16-3,21.29-.08,5.36-.19,12.71-2.15,14.66s-9.3,2.07-14.66,2.15c-7.13.11-14.49.22-21.29,3-7,2.91-12.73,8.37-17.76,13.19C135.78,214.84,130.4,220,128,220s-7.78-5.16-11.34-8.57c-5-4.82-10.72-10.28-17.76-13.19-6.8-2.82-14.16-2.93-21.29-3-5.36-.08-12.71-.19-14.66-2.15s-2.07-9.3-2.15-14.66c-.11-7.13-.22-14.49-3-21.29-2.91-7-8.37-12.73-13.19-17.76C41.16,135.78,36,130.4,36,128s5.16-7.78,8.57-11.34c4.82-5,10.28-10.72,13.19-17.76,2.82-6.8,2.93-14.16,3-21.29C60.88,72.25,61,64.9,63,63s9.3-2.07,14.66-2.15c7.13-.11,14.49-.22,21.29-3,7-2.91,12.73-8.37,17.76-13.19C120.22,41.16,125.6,36,128,36s7.78,5.16,11.34,8.57c5,4.82,10.72,10.28,17.76,13.19,6.8,2.82,14.16,2.93,21.29,3,5.36.08,12.71.19,14.66,2.15s2.07,9.3,2.15,14.66c.11,7.13.22,14.49,3,21.29,2.91,7,8.37,12.73,13.19,17.76,3.41,3.56,8.57,8.94,8.57,11.34S214.84,135.78,211.43,139.34ZM176.49,95.51a12,12,0,0,1,0,17l-56,56a12,12,0,0,1-17,0l-24-24a12,12,0,1,1,17-17L112,143l47.51-47.52A12,12,0,0,1,176.49,95.51Z"/>
        </svg>
      </div>
      <span
        className="text-lg font-medium text-balance"
        dangerouslySetInnerHTML={{ __html: feature }}
      />
    </div>
  );
};

const ComeFunzionaPage: React.FC = () => {
  useSEO({
    title: 'Come Funziona Synergy - Aggregazione Ordini Materiali Edili',
    description: 'Scopri come Synergy semplifica l\'approvvigionamento dei materiali edili attraverso l\'aggregazione degli ordini. Processo in 3 step per ottenere prezzi migliori.',
    keywords: 'come funziona synergy, aggregazione ordini, materiali edili, processo acquisto, ordini collettivi, risparmio materiali, fornitori certificati',
    ogTitle: 'Come Funziona Synergy - Aggregazione Ordini Materiali Edili',
    ogDescription: 'Scopri come Synergy semplifica l\'approvvigionamento dei materiali edili attraverso l\'aggregazione degli ordini.',
    canonicalUrl: 'https://synergyedilizia.com/come-funziona',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "Come Funziona Synergy - Aggregazione Ordini Materiali Edili",
      "description": "Processo in 3 step per ottenere prezzi migliori sui materiali edili",
      "step": [
        {
          "@type": "HowToStep",
          "name": "Obiettivi di ordine",
          "text": "Pù cresce l'ordine complessivo, più sconti esclusivi si attivano."
        },
        {
          "@type": "HowToStep", 
          "name": "Aggregazione",
          "text": "La tua richiesta viene unita a quella di altre imprese, formando un unico grande ordine e accrescendo così il tuo potere contrattuale."
        },
        {
          "@type": "HowToStep",
          "name": "Condizioni vantaggiose", 
          "text": "Grazie al volume complessivo otteni un prezzo più basso, che si traduce in un risparmio concreto per te e per la tua azienda."
        }
      ]
    }
  });

  const steps = [
    {
      title: "Obiettivi di ordine",
      description: "Più cresce l'ordine complessivo, più sconti esclusivi si attivano.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      illustrationPath: "/assets/icons/target-bold.svg"
    },
    {
      title: "Aggregazione",
      description: "La tua richiesta viene unita a quella di altre imprese, accrescendo così il tuo potere contrattuale.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      illustrationPath: "/assets/icons/bezier-curve-bold.svg"
    },
    {
      title: "Condizioni vantaggiose",
      description: "Grazie al volume complessivo otteni un prezzo più basso, che si traduce in un risparmio concreto per te e per la tua azienda.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      illustrationPath: "/assets/icons/seal-percent-bold.svg"
    }
  ];

  const benefits = [
    {
      text: "Prezzi ridotti sui materiali",
      description: "Negoziamo direttamente con i produttori per ottenere condizioni di acquisto esclusive.",
      icon: <TagIcon />
    },
    {
      text: "Fornitori affidabili e certificati",
      description: "Collaboriamo solo con aziende che rispettano rigorosi standard di produzione.",
      icon: <CertificateIcon />
    },
    {
      text: "Paghi solo sul risparmio ottenuto",
      description: "Synergy non ha costi fissi né abbonamenti. Vinciamo insieme.",
      icon: <HandCoinsIcon />
    },
    {
      text: "Nessun compromesso sulla qualità",
      description: "Ottieni materiali di primo livello, selezionati tra i migliori produttori del settore.",
      icon: <SparkleIcon />
    },
  ];

  const portalFeatures = [
    "Visualizzare le offerte in tempo reale",
    "Rimanere aggiornato sui nuovi obiettivi",
    "Gestire i tuoi<br>ordini in autonomia"
  ];

  return (
    <div className="pt-20 bg-white">
      {/* Hero Section */}
      <section id="how-it-work" className="pt-20 pb-12 md:pb-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Come funziona
            </h1>
            <p className="text-md sm:text-lg text-gray-600 leading-relaxed text-balance">
              Synergy semplifica l'approvvigionamento dei materiali edili attraverso un processo innovativo di aggregazione degli ordini.
            </p>
          </div>
        </div>

        <div className="container pt-16 pb-12 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="timeline">
              <div className="timeline-items">
                {steps.map((step, index) => (
                  <div key={index} className="timeline-item">
                    <div className="timeline-dot"></div>
                    <div className="timeline-line"></div>
                    <StepCard
                      icon={step.icon}
                      title={step.title}
                      description={step.description}
                      illustrationPath={step.illustrationPath}
                      animationDelay={index * 200}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portale Web Section */}
      <section id="synergy-hub" className="pt-40 pb-36 md:pt-48 md:pb-52 bg-primary relative">
        <div className="absolute top-0 left-0 w-full h-full">
          <Silk
            speed={5}
            scale={0.75}
            color="#2C63DD"
            noiseIntensity={0.75}
            rotation={0}
          />
        </div>
        <div className="container mx-auto px-6 lg:px-8 relative">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-block bg-white/20 rounded-full px-4 py-2 text-sm font-semibold mb-6 backdrop-blur-lg">
              In arrivo
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold mb-10">
              Synergy Hub
            </h2>
            <p className="text-xl text-blue-50 mb-10 leading-relaxed">
            Un unico spazio digitale che unisce efficienza, trasparenza e controllo, dove potrai:
            </p>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {portalFeatures.map((feature, index) => (
                <PortalFeatureCard 
                  key={index}
                  feature={feature}
                  index={index}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section id="why-synergy" className="py-28 md:py-48 bg-white">
        <div className="container mx-auto xl:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-16 text-center text-balance">
              Perché conviene con Synergy?
            </h2>
            <div className="grid md:grid-cols-2 gap-8 xl:px-4">
              {benefits.map((benefit, index) => (
                <BenefitCard 
                  key={index}
                  benefit={benefit}
                  index={index}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <ContactForm />
    </div>
  );
};

export default ComeFunzionaPage;
